import { React, useEffect, useState } from 'react';
import { FiPaperclip, FiDownload } from 'react-icons/fi';
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
  FaFileAudio,
  FaFileVideo,
  FaFileArchive,
  FaFile,
} from 'react-icons/fa';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import toast from 'react-hot-toast';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi';
import { FiMaximize } from 'react-icons/fi';
import { LuReply, LuForward } from 'react-icons/lu';

import { DataState } from '../../context/DataProvider';
import AIEmailView from '../chat/AIEmailView';
import ReplyEmail from './ReplyEmail';
import { API_URL } from '../../services/api';
import InsightsPanel from './InsightsPanel';
import ActionsPanel from './ActionsPanel';
import ForwardEmail from '../email/ForwardEmail';
import EmailIframe from '../chat/EmailIframe';

export const getFileIcon = (mimeType) => {
  if (mimeType?.includes('pdf'))
    return <FaFilePdf className="text-red-500" size={20} />;
  if (mimeType?.includes('word'))
    return <FaFileWord className="text-[#5652CF]" size={20} />;
  if (mimeType?.includes('excel'))
    return <FaFileExcel className="text-green-500" size={20} />;
  if (mimeType?.includes('image'))
    return <FaFileImage className="text-yellow-500" size={20} />;
  if (mimeType?.includes('zip') || mimeType?.includes('rar'))
    return <FaFileArchive className="text-gray-500" size={20} />;
  if (mimeType?.includes('audio'))
    return <FaFileAudio className="text-purple-500" size={20} />;
  if (mimeType?.includes('video'))
    return <FaFileVideo className="text-indigo-500" size={20} />;
  return <FaFile className="text-gray-500" size={20} />;
};

export const handleDownload = async (
  messageId,
  attachmentId,
  fileName,
  accessToken,
  isOutlook,
) => {
  try {
    const endpoint = isOutlook
      ? `${API_URL}/outlook_mails/emails/${messageId}/attachments/${attachmentId}`
      : `${API_URL}/gmail_emails/emails/${messageId}/attachments/${attachmentId}`;

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
      credentials: 'include',
    });

    if (!response.ok) throw new Error('Failed to fetch attachment');

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success(`Downloaded: ${fileName}`);
  } catch (error) {
    toast.error(
      typeof error === 'string' ? error : error?.message || 'Download Failed',
    );
  }
};

export const AttachmentItem = ({ messageId, attachment, isOutlook }) => {
  const { userGoogleId } = DataState();
  const [hovered, setHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Normalise field names — Outlook stores {id, name, contentType}, Gmail stores {attachmentId, filename, mimeType}
  const attachmentId = attachment.attachmentId || attachment.id;
  const filename = attachment.filename || attachment.name || 'attachment';
  const mimeType = attachment.mimeType || attachment.contentType || '';

  const handleDownloadClick = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await handleDownload(
        messageId,
        attachmentId,
        filename,
        userGoogleId,
        isOutlook,
      );
    } catch (error) {
      toast.error('Failed to download file.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className="relative inline-flex h-10 w-44 cursor-pointer items-center rounded-lg border p-2 py-3 text-gray-500 shadow-md"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Always-visible: icon + filename */}
      <div className="flex w-full items-center space-x-2 overflow-hidden pr-6">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center">
          {getFileIcon(mimeType || filename)}
        </div>
        <span className="truncate text-sm font-medium">{filename}</span>
      </div>

      {/* Download button — appears on right side on hover */}
      {hovered && (
        <div className="absolute right-2 flex items-center">
          {isDownloading ? (
            <AiOutlineLoading3Quarters
              className="animate-spin text-gray-600"
              size={14}
            />
          ) : (
            <FiDownload
              onClick={handleDownloadClick}
              className="cursor-pointer text-gray-600 hover:text-black"
              size={14}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default function EmailContent({
  emailThread = [],
  email,
  loading,
  isAnalyticsView,
  messageId,
  isDialog,
  isOutlook,
  setIsFullScreen,
  isConversationThread = false,
}) {
  const { userInfo } = DataState();
  const loggedInEmail = userInfo?.email || '';

  // merge recipients
  const toList = email?.to?.map((r) => r.email) || [];
  const ccList = email?.cc?.map((r) => r.email) || [];
  const allRecipients = [...new Set([...toList, ...ccList])];

  // filter self out
  const otherRecipients = allRecipients.filter(
    (r) => r?.toLowerCase() !== loggedInEmail?.toLowerCase(),
  );

  // true if more than 1 person besides you
  const [showReplyAllPanel, setShowReplyAllPanel] = useState(false);

  const showReplyAll = otherRecipients.length > 0;
  const [showReply, setShowReply] = useState(false);
  const [showInsightsPanel, setShowInsightsPanel] = useState(false);
  const [showActionsPanel, setShowActionsPanel] = useState(false);
  const [showForwardPanel, setShowForwardPanel] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const visibleCount = 3;

  //state variables for reply email
  const [messageBody, setMessageBody] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [localAttachments, setLocalAttachments] = useState([]);
  const [driveAttachments, setDriveAttachments] = useState([]);

  //state variables for forward email
  const [message, setMessage] = useState('');
  const [recepientsEmails, setRecepientsEmails] = useState([]);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (showForwardPanel && email?.body) {
      // This is the core logic: set the message state to the email body.
      // You can also add a placeholder for a new comment here.
      // setMessage(`
      //     <div>
      //           ${email.body}
      //         <hr>

      //     </div>
      // `);
      setMessage('');
    } else {
      // Optional: Clear the message when the panel is closed
      setMessage('');
    }
  }, [showForwardPanel, email]); // Dependencies: run this effect when these states/props change
  return !isConversationThread ? (
    <div className="mt-4 flex w-full flex-row">
      <div
        className={`hidden rounded px-4 pl-0 md:block
        ${loading && 'animate-pulse text-gray-800'}
      `}
      >
        <div
          className={`flex h-10  w-10 items-center justify-center rounded-full text-sm font-bold text-white
          ${loading ? 'bg-gray-300' : 'bg-gray-700'}
        `}
        >
          {email.from?.map((from, index) => {
            return <span key={index}>{from.name[0].toUpperCase()}</span>;
          })}
        </div>
      </div>

      <div className="flex w-full flex-col">
        <div className="flex flex-col justify-between md:flex-row">
          {/* Sender and Recipient Info */}
          <div className="flex text-gray-700 md:mr-4">
            <div
              className={`rounded px-4 pl-0 md:hidden
        ${loading && 'animate-pulse text-gray-800'}
      `}
            >
              <div
                className={`flex h-10  w-10 items-center justify-center rounded-full text-sm font-bold text-white
          ${loading ? 'bg-gray-300' : 'bg-gray-700'}
        `}
              >
                {email.from?.map((from, index) => {
                  return <span key={index}>{from.name[0].toUpperCase()}</span>;
                })}
              </div>
            </div>
            <div className="flex flex-col">
              {loading ? (
                <div className="h-4 w-32 animate-pulse rounded bg-gray-300"></div>
              ) : (
                email.from?.map((from, index) => (
                  <span key={index} className="font-semibold">
                    {from.name}{' '}
                    <span className="text-sm font-normal text-gray-500">
                      {from.email || 'No Sender'}
                    </span>
                  </span>
                ))
              )}

              <div className="flex flex-wrap">
                {loading ? (
                  <div className="mt-2 h-4 w-24 animate-pulse rounded bg-gray-300"></div>
                ) : (
                  <>
                    <span className="mr-2 text-gray-500">to</span>
                    <div className="flex flex-wrap items-center">
                      {email.to?.slice(0, visibleCount).map((to, index) => (
                        <span
                          key={index}
                          className="mr-1 font-semibold"
                          title={to?.email || 'No Recipient'}
                        >
                          {to?.name || to?.email || 'No Recipient'}
                          {index <
                            Math.min(visibleCount, email.to?.length) - 1 && ','}
                        </span>
                      ))}
                      {email.to?.length > visibleCount && (
                        <button
                          className="ml-1 flex items-center text-[#5652CF] hover:underline"
                          onClick={() => setShowAll(!showAll)}
                        >
                          {showAll
                            ? 'show less'
                            : `+${email.to?.length - visibleCount}`}
                          {showAll ? (
                            <HiChevronUp size={14} />
                          ) : (
                            <HiChevronDown size={14} />
                          )}
                        </button>
                      )}
                    </div>
                  </>
                )}

                {/* Hidden section for extra emails */}
                <div
                  className={`mt-2 w-full rounded-xl border border-gray-400 p-2 font-semibold text-gray-500 transition-all duration-300 ease-in-out ${
                    showAll
                      ? 'max-h-40 opacity-100'
                      : 'max-h-0 overflow-hidden opacity-0'
                  }`}
                >
                  {email.to?.slice(visibleCount).map((to, index) => (
                    <span key={index} className="block text-sm text-gray-700">
                      {to.name || to.email || 'No Recipient'}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Meta Info and Icons */}
          <div className="flex flex-col text-sm sm:gap-2 md:items-end">
            <div className="flex flex-wrap justify-between gap-2 md:justify-normal">
              <div className="flex gap-2">
                <div className="mb-2 flex items-center gap-2">
                  {loading ? (
                    <div className="h-4 w-32 animate-pulse rounded bg-gray-300"></div>
                  ) : (
                    <span className="text-sm text-gray-500">
                      {email.date || ''}
                    </span>
                  )}
                </div>
                <div className="mb-2 flex items-center">
                  {loading ? (
                    <div className="h-4 w-12 animate-pulse rounded bg-gray-300"></div>
                  ) : (
                    <span className="text-sm text-gray-500">
                      {email.time || ''}
                    </span>
                  )}
                </div>
              </div>
              {/* {!loading && (
                                    <div className="flex gap-2">
                                        <button className="mb-2 text-gray-500 hover:text-gray-700">
                                            <FaRegStar />
                                        </button>
                                        <button className="mb-2 text-gray-500 hover:text-gray-700">
                                            <FiSmile />
                                        </button>
                                        <button className="mb-2 text-gray-500 hover:text-gray-700">
                                            <PiArrowBendUpLeftBold />
                                        </button>
                                        <button className="mb-2 text-gray-500 hover:text-gray-700">
                                            <BsThreeDotsVertical />
                                        </button>
                                    </div>
                                )} */}
            </div>

            {/* Priority Tag */}
            <div className="mb-2 flex md:justify-end">
              {loading ? (
                <div className="h-6 w-16 animate-pulse rounded bg-gray-300"></div>
              ) : (
                <span
                  className={`rounded-md px-3 py-1 text-xs font-semibold text-white 
            ${
              email?.priority === 1 || email?.priority === 0
                ? 'bg-blue-400'
                : email?.priority >= 2 && email?.priority <= 3
                  ? 'bg-yellow-400'
                  : email?.priority >= 4
                    ? 'bg-red-400'
                    : 'bg-gray-500'
            }`}
                >
                  {email?.priority === 1 || email?.priority === 0
                    ? 'Low'
                    : email?.priority >= 2 && email?.priority <= 3
                      ? 'Medium'
                      : email?.priority >= 4
                        ? 'High'
                        : 'Unknown'}
                </span>
              )}
            </div>
          </div>
        </div>
        {isAnalyticsView ? (
          <AIEmailView email={email} />
        ) : (
          <div className="relative flex w-full">
            {loading ? (
              <div className="absolute bottom-2 right-10 h-8 w-8 animate-pulse rounded-full bg-gray-500"></div> // Placeholder while loading
            ) : (
              <button
                className="absolute bottom-2 right-10 transform rounded-full bg-gray-200 p-2 text-gray-500 transition-all hover:scale-110 hover:bg-gray-300 hover:text-gray-700"
                onClick={() => setIsFullScreen(true)}
              >
                <FiMaximize size={20} />
              </button>
            )}

            <div className="no-scrollbar max-h-96 flex-1 overflow-y-auto md:mr-4">
              {loading ? (
                <div className="h-[300px] w-full animate-pulse rounded bg-gray-300"></div> // Placeholder while loading
              ) : (
                <EmailIframe emailBody={email.body}></EmailIframe>
              )}
            </div>
          </div>
        )}
        <div className="mt-4">
          <div className="mb-2 flex flex-col justify-between gap-4 border-t border-gray-300 px-4 py-4 sm:flex-row sm:items-center">
            {/* Left: Attachment Indicator */}
            <div className="flex items-center">
              {loading ? (
                <>
                  <div className="mr-2 h-6 w-6 animate-pulse rounded bg-gray-300"></div>
                  <div className="h-6 w-24 animate-pulse rounded bg-gray-300"></div>
                </>
              ) : (
                <>
                  <FiPaperclip className="mr-2 text-gray-500" size={20} />
                  <span className="text-gray-500">Attachments</span>
                </>
              )}
            </div>

            {/* Right: Action Buttons */}
            {isDialog && (
              <div className="flex flex-wrap justify-start gap-2 sm:justify-end sm:gap-3">
                {loading ? (
                  [...Array(5)].map((_, index) => (
                    <div
                      key={index}
                      className="h-8 w-20 animate-pulse rounded bg-gray-300"
                    ></div>
                  ))
                ) : (
                  <>
                    {/* <button
                    className={`flex items-center px-3 py-1.5 rounded-md border transition-all text-sm font-medium ${
                        false
                            ? "bg-[#5652CF] text-white"
                            : "bg-white border-gray-300 text-gray-600 hover:bg-gray-200"
                    }`}
                >
                    Validate
                </button> */}

                    <button
                      className={`flex items-center rounded-md border px-3 py-1.5 text-sm font-medium transition-all ${
                        showInsightsPanel
                          ? 'bg-[#5652CF] text-white'
                          : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-200'
                      }`}
                      onClick={() => {
                        setShowInsightsPanel(true);
                        setShowActionsPanel(false);
                        setShowReply(false);
                        setShowReplyAllPanel(false);
                        setShowForwardPanel(false);
                      }}
                    >
                      Timeline
                    </button>

                    <button
                      className={`flex items-center rounded-md border px-3 py-1.5 text-sm font-medium transition-all ${
                        showActionsPanel
                          ? 'bg-[#5652CF] text-white'
                          : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-200'
                      }`}
                      onClick={() => {
                        setShowInsightsPanel(false);
                        setShowActionsPanel(true);
                        setShowReply(false);
                        setShowReplyAllPanel(false);
                        setShowForwardPanel(false);
                      }}
                    >
                      Actions
                    </button>

                    <button
                      className={`flex items-center rounded-md border px-3 py-1.5 text-sm font-medium transition-all ${
                        showReply
                          ? 'bg-[#5652CF] text-white'
                          : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-200'
                      }`}
                      onClick={() => {
                        setShowInsightsPanel(false);
                        setShowActionsPanel(false);
                        setShowReply(true);
                        setShowReplyAllPanel(false);
                        setShowForwardPanel(false);
                      }}
                    >
                      <LuReply size={20} className="mr-1" />
                      Reply
                    </button>

                    {showReplyAll && (
                      <button
                        className={`flex items-center rounded-md border px-3 py-1.5 text-sm font-medium transition-all ${
                          showReplyAllPanel
                            ? 'bg-[#5652CF] text-white'
                            : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-200'
                        }`}
                        onClick={() => {
                          setShowInsightsPanel(false);
                          setShowActionsPanel(false);
                          setShowReply(false);
                          setShowReplyAllPanel(true);
                          setShowForwardPanel(false);
                        }}
                      >
                        <LuReply size={20} className="mr-1" />
                        Reply All
                      </button>
                    )}

                    <button
                      className={`flex items-center rounded-md border px-3 py-1.5 text-sm font-medium transition-all ${
                        showForwardPanel
                          ? 'bg-[#5652CF] text-white'
                          : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-200'
                      }`}
                      onClick={() => {
                        setShowInsightsPanel(false);
                        setShowActionsPanel(false);
                        setShowReply(false);
                        setShowReplyAllPanel(false);
                        setShowForwardPanel(true);
                      }}
                    >
                      <LuForward size={20} className="mr-1" />
                      Forward
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {email.attachments && email.attachments.length > 0 && (
            <div className="mr-4 flex min-h-[40px] flex-grow  flex-wrap items-center gap-2 border-b border-gray-300 pb-5 ">
              {email.attachments.map((att) => (
                <AttachmentItem
                  key={att.attachmentId}
                  messageId={messageId}
                  attachment={att}
                  isOutlook={isOutlook}
                />
              ))}
            </div>
          )}

          {isDialog && (
            <>
              {showReply && (
                <div className="mb-2 flex w-full items-center justify-between p-4 pl-0">
                  <ReplyEmail
                    sender={email.from}
                    message_id={email?.message_id}
                    onClose={() => setShowReply(false)} // Corrected line
                    messageBody={messageBody}
                    setMessageBody={setMessageBody}
                    localAttachments={localAttachments}
                    driveAttachments={driveAttachments}
                    setLocalAttachments={setLocalAttachments}
                    setDriveAttachments={setDriveAttachments}
                    customPrompt={customPrompt}
                    setCustomPrompt={setCustomPrompt}
                    isOutlook={isOutlook}
                  />
                </div>
              )}

              {showReplyAllPanel && (
                <div className="mb-2 flex w-full items-center justify-between p-4 pl-0">
                  <ReplyEmail
                    sender={[...email.from, ...email.to, ...email.cc]} // include all recipients
                    message_id={email?.message_id}
                    onClose={() => setShowReplyAllPanel(false)} // Corrected line
                    setShowReplyAllPanel={setShowReplyAllPanel}
                    messageBody={messageBody}
                    setMessageBody={setMessageBody}
                    localAttachments={localAttachments}
                    driveAttachments={driveAttachments}
                    setLocalAttachments={setLocalAttachments}
                    setDriveAttachments={setDriveAttachments}
                    customPrompt={customPrompt}
                    setCustomPrompt={setCustomPrompt}
                    isOutlook={isOutlook}
                  />
                </div>
              )}

              {showInsightsPanel && (
                <div className="mb-2 flex w-full items-center justify-between p-4 pl-0">
                  <InsightsPanel messages={emailThread} />
                </div>
              )}

              {showActionsPanel && (
                <div className="mb-2 flex w-full items-center justify-between p-4 pl-0">
                  <ActionsPanel
                    messageId={messageId}
                    isOutlook={isOutlook}
                    actionItems={email?.actionItems || []}
                  />
                </div>
              )}

              {showForwardPanel && (
                <div className="mb-2 flex w-full items-center justify-between p-4 pl-0">
                  <ForwardEmail
                    message_id={email?.message_id}
                    setShowForwardPanel={setShowForwardPanel}
                    message={message} // The message state is now correctly populated
                    setMessage={setMessage}
                    recepientsEmails={recepientsEmails}
                    setRecepientsEmails={setRecepientsEmails}
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    isOutlook={isOutlook}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  ) : (
    <>
      {isAnalyticsView ? (
        <AIEmailView email={email} />
      ) : (
        <div className="relative flex w-full">
          {loading ? (
            <div className="absolute bottom-2 right-10 h-8 w-8 animate-pulse rounded-full bg-gray-500"></div> // Placeholder while loading
          ) : (
            <button
              className="absolute bottom-2 right-10 transform rounded-full bg-gray-200 p-2 text-gray-500 transition-all hover:scale-110 hover:bg-gray-300 hover:text-gray-700"
              onClick={() => setIsFullScreen(true)}
            >
              <FiMaximize size={20} />
            </button>
          )}

          <div className="mr-4 max-h-96 flex-1 overflow-y-auto">
            {loading ? (
              <div className="h-[300px] w-full animate-pulse rounded bg-gray-300"></div> // Placeholder while loading
            ) : (
              <EmailIframe emailBody={email.body}></EmailIframe>
            )}
          </div>
        </div>
      )}
      <div className="mt-4">
        <div className="mb-2 mr-4 flex items-center justify-between border-t border-gray-300 p-4 pl-0 pr-0">
          <div className="flex items-center">
            {loading ? (
              <>
                <div className="mr-2 h-6 w-6 animate-pulse rounded bg-gray-300"></div>
                <div className="h-6 w-24 animate-pulse rounded bg-gray-300"></div>
              </>
            ) : (
              <>
                <FiPaperclip className="mr-2 text-gray-500" size={20} />
                <span className="text-gray-500">Attachments</span>
              </>
            )}
          </div>

          {/* Reply & Forward Buttons */}
          {isDialog && (
            <div className="flex space-x-2">
              {loading ? (
                <>
                  {[...Array(5)].map((_, index) => (
                    <div
                      key={index}
                      className="h-8 w-20 animate-pulse rounded bg-gray-300"
                    ></div>
                  ))}
                </>
              ) : (
                <>
                  <button
                    className="flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-600 transition-all hover:bg-gray-200"
                    // onClick={() => setShowReply(!showReply)}
                  >
                    <span className="text-sm font-medium">Validate</span>
                  </button>

                  <button
                    className="flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-600 transition-all hover:bg-gray-200"
                    onClick={() => {
                      setShowInsightsPanel(true);
                      setShowActionsPanel(false);
                      setShowReply(false);
                      setShowForwardPanel(false);
                    }}
                  >
                    <span className="text-sm font-medium">Insights</span>
                  </button>

                  <button
                    className="flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-600 transition-all hover:bg-gray-200"
                    onClick={() => {
                      setShowInsightsPanel(false);
                      setShowActionsPanel(true);
                      setShowReply(false);
                      setShowForwardPanel(false);
                    }}
                  >
                    <span className="text-sm font-medium">Actions</span>
                  </button>

                  <button
                    className="flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-600 transition-all hover:bg-gray-200"
                    onClick={() => {
                      setShowInsightsPanel(false);
                      setShowActionsPanel(false);
                      setShowReply(true);
                      setShowForwardPanel(false);
                    }}
                  >
                    <LuReply size={20} className="mr-1" />
                    <span className="text-sm font-medium">Reply</span>
                  </button>

                  <button
                    className="flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-600 transition-all hover:bg-gray-200"
                    onClick={() => {
                      setShowInsightsPanel(false);
                      setShowActionsPanel(false);
                      setShowReply(false);
                      setShowForwardPanel(true);
                    }}
                  >
                    <LuForward size={20} className="mr-1" />
                    <span className="text-sm font-medium">Forward</span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {email.attachments && email.attachments.length > 0 && (
          <div className="mr-4 flex min-h-[40px] flex-grow  flex-wrap items-center gap-2 border-b border-gray-300 pb-5 ">
            {email.attachments.map((att) => (
              <AttachmentItem
                key={att.attachmentId}
                messageId={messageId}
                attachment={att}
                isOutlook={isOutlook}
              />
            ))}
          </div>
        )}

        {isDialog && (
          <>
            {showReply && (
              <div className="mb-2 flex w-full items-center justify-between p-4 pl-0">
                <ReplyEmail
                  sender={[email.from]}
                  message_id={email?.message_id}
                  setShowReply={setShowReply}
                  messageBody={messageBody}
                  setMessageBody={setMessageBody}
                  localAttachments={localAttachments}
                  driveAttachments={driveAttachments}
                  setLocalAttachments={setLocalAttachments}
                  setDriveAttachments={setDriveAttachments}
                  customPrompt={customPrompt}
                  setCustomPrompt={setCustomPrompt}
                />
              </div>
            )}

            {showInsightsPanel && (
              <div className="mb-2 flex w-full items-center justify-between p-4 pl-0">
                <InsightsPanel messages={emailThread} />
              </div>
            )}
            {showActionsPanel && (
              <div className="mb-2 flex w-full items-center justify-between p-4 pl-0">
                <ActionsPanel
                  messageId={messageId}
                  actionItems={email?.actionItems || []}
                />
              </div>
            )}
            {showForwardPanel && (
              <div className="mb-2 flex w-full items-center justify-between p-4 pl-0">
                <ForwardEmail
                  message_id={email?.message_id}
                  setShowForwardPanel={setShowForwardPanel}
                  message={message}
                  setMessage={setMessage}
                  recepientsEmails={recepientsEmails}
                  setRecepientsEmails={setRecepientsEmails}
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
